import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHorizontalScroll } from "../../../../../hooks/useHorizontalScroll";
import useMirrorScrollbar from "../../../../../hooks/useMirrorScrollbar";
import {
  getMonthsWeekData,
  hasWideWeeklyStatValue,
} from "../../../../../utils/annual-planner-helpers";
import {
  AthleteStatsHeader,
  MilestoneCounter,
  MilestoneLegend,
  MilestoneRowHeader,
  PhaseCard,
  PhaseRowHeader,
  PhasesDescriptionBubbles,
  PhaseTrackTitle,
  PlannerTitleRow,
  StatsCard,
  WeekOfRow,
} from "./";

// Row-group-first grid (see ~/.claude/plans/periodization-calendar-view-redesign.md,
// "un solo contenedor" follow-up, 2026-09-03). Every row-group (Week of,
// Training/Nutrition Phase, Notes, Milestones, Tracked Results) is its own
// [.planner-headers label | .planner-body months] pair, stacked vertically
// inside one .planner-grid instead of the old single tall .planner-headers
// sidebar + single tall .planner-body of full-month columns. This is what
// lets a title bar (PhaseTrackTitle) render as a plain full-width row
// between two such pairs, spanning label+months with nothing to align by
// hand - and it's why the old MonthColumn.js (one component owning an
// entire month's whole vertical stack) isn't used here anymore; each row
// below calls its own body piece directly, once per month. MonthColumn.js
// itself is untouched - NutritionPlanCalendar.js still uses it as-is.
//
// .planner-grid is the single horizontally-scrolling element (previously
// that was just .planner-body); .planner-headers cells are sticky-left
// within it, same visual effect as the old fixed sidebar. `div[role="month-
// column"]` still wraps each month's piece per row (now one piece each,
// not a whole stack) purely so the large existing set of CSS selectors
// scoped under it keeps matching unchanged.
export const CalendarView = ({
  phases,
  nutritionPhases,
  nutritionPhasesAttachments,
  handleNutritionPhaseSelection,
  milestones,
  events,
  units,
  athleteStatsHeaders,
  dateFromState,
  startingCalendarDay,
  phasesAttachments,
  handlePhaseSelection,
  phasesDescriptions,
  setMirrorScrollbar,
  athleteStatsState,
  athleteVolumesState,
  selectedWeek,
  onSelectWeek,
}) => {
  const { t } = useTranslation("athlete-portal");

  // Training/Nutrition Phase sections collapse independently - owned here
  // (not lifted to AnnualPlanner) since it's purely a Calendar view display
  // concern; List view has its own columns and doesn't need it.
  const [isTrainingCollapsed, setIsTrainingCollapsed] = useState(false);
  const [isNutritionCollapsed, setIsNutritionCollapsed] = useState(false);
  const [isMilestonesCollapsed, setIsMilestonesCollapsed] = useState(false);
  const [isNotesCollapsed, setIsNotesCollapsed] = useState(false);
  // Collapsed by default in the grid, per PRD 7.6 - unlike the other
  // sections above, which default to expanded.
  const [isStatsCollapsed, setIsStatsCollapsed] = useState(true);

  //   Horizontal scroll for the calendar view - now on .planner-grid itself
  // (the whole grid, labels included) rather than just the old .planner-body,
  // since labels are sticky-positioned inside this same scroll container.
  const horizontalScrollRef = useHorizontalScroll("faster");

  // Mirror scrollbar (see useMirrorScrollbar.js) - the real scrollbar on
  // .planner-grid is hidden in CSS, this sticky-bottom strip (rendered by
  // AnnualPlanner.js as a sibling of <article id="annual-planner"> itself,
  // not here - see its own comment on why) is the only one ever visible.
  const {
    scrollRef: mirrorScrollRef,
    mirrorRef,
    needsScroll,
    scrollWidth,
    onScrollRef,
    onScrollMirror,
  } = useMirrorScrollbar([phases, nutritionPhases]);

  // Both useHorizontalScroll's wheel-drag ref and useMirrorScrollbar's sync
  // ref need to point at the same .planner-grid node - React only takes one
  // `ref` prop, so this callback ref fans out to both.
  const setGridRef = (node) => {
    horizontalScrollRef.current = node;
    mirrorScrollRef.current = node;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mirrorRef/setMirrorScrollbar are stable; onScrollMirror is a fresh function every render (see useMirrorScrollbar.js), so including it here would re-run this effect (and re-render AnnualPlanner) on every render, forever
  useEffect(() => {
    setMirrorScrollbar({ mirrorRef, needsScroll, scrollWidth, onScrollMirror });
  }, [needsScroll, scrollWidth]);

  // numberOfWeeks only depends on (month, startingCalendarDay) - computed
  // once here instead of separately inside each of the 5 row-groups' own
  // months.map() below, which all needed the exact same value per month.
  // Memoized (not just a plain const) so hasWideStatValue below - which
  // depends on this array's identity - doesn't recompute on every render.
  const monthsData = useMemo(
    () => getMonthsWeekData(dateFromState, startingCalendarDay),
    [dateFromState, startingCalendarDay]
  );

  // Whether the current week specifically has a Weekly Volume or Bodyweight
  // value wider than Tracked Results' own fixed box - only Tracked Results
  // reacts to this (shrinks its own font-size/line-height, see
  // hasWideWeeklyStatValue's own comment) - every other row-group keeps its
  // default size regardless.
  const hasWideStatValue = useMemo(
    () =>
      hasWideWeeklyStatValue(
        monthsData,
        startingCalendarDay,
        athleteStatsState,
        athleteVolumesState,
        units
      ),
    [
      monthsData,
      startingCalendarDay,
      athleteStatsState,
      athleteVolumesState,
      units,
    ]
  );

  // Sticky-left .planner-headers only needs an opaque background while
  // actually offset from its own column (scrollLeft > 0) - at rest, with
  // nothing scrolled behind it yet, the plain (no-background) look stays.
  // Toggled straight on the DOM node (not React state) so this doesn't
  // re-render the whole grid on every scroll tick.
  const handleGridScroll = (e) => {
    onScrollRef(e);
    e.currentTarget.classList.toggle(
      "is-scrolled",
      e.currentTarget.scrollLeft > 0
    );
  };

  return (
    <div className="planner-grid" ref={setGridRef} onScroll={handleGridScroll}>
      <div className="planner-headers">
        <div className="month-row-header">
          <span className="row-header">{t("annualPlanner.weekOf")}</span>
        </div>
      </div>
      <div className="planner-body week-of-body">
        {monthsData.map(({ month, numberOfWeeks }) => {
          return (
            <div role="month-column" key={month.format("YYYYMM")}>
              <WeekOfRow
                month={month}
                startingCalendarDay={startingCalendarDay}
                numberOfWeeks={numberOfWeeks}
                selectedWeek={selectedWeek}
                onSelectWeek={(week) => onSelectWeek(week, { silent: true })}
              />
            </div>
          );
        })}
      </div>

      <PhaseTrackTitle
        phases={phases}
        track="training"
        isCollapsed={isTrainingCollapsed}
        onToggleCollapse={() => setIsTrainingCollapsed((prev) => !prev)}
      />
      {!isTrainingCollapsed && (
        <React.Fragment>
          <div className="planner-headers">
            <PhaseRowHeader phases={phases} track="training" />
          </div>
          <div className="planner-body">
            {monthsData.map(({ month, numberOfWeeks }) => {
              return (
                <div role="month-column" key={month.format("YYYYMM")}>
                  <PhaseCard
                    numberOfWeeks={numberOfWeeks}
                    month={month}
                    phases={phases}
                    startingCalendarDay={startingCalendarDay}
                    phasesAttachments={phasesAttachments}
                    handlePhaseSelection={handlePhaseSelection}
                  />
                </div>
              );
            })}
          </div>
        </React.Fragment>
      )}

      <PhaseTrackTitle
        phases={nutritionPhases}
        track="nutrition"
        isCollapsed={isNutritionCollapsed}
        onToggleCollapse={() => setIsNutritionCollapsed((prev) => !prev)}
      />
      {!isNutritionCollapsed && (
        <React.Fragment>
          <div className="planner-headers">
            <PhaseRowHeader phases={nutritionPhases} track="nutrition" />
          </div>
          <div className="planner-body">
            {monthsData.map(({ month, numberOfWeeks }) => {
              return (
                <div role="month-column" key={month.format("YYYYMM")}>
                  <PhaseCard
                    numberOfWeeks={numberOfWeeks}
                    month={month}
                    phases={nutritionPhases}
                    startingCalendarDay={startingCalendarDay}
                    phasesAttachments={nutritionPhasesAttachments}
                    handlePhaseSelection={handleNutritionPhaseSelection}
                  />
                </div>
              );
            })}
          </div>
        </React.Fragment>
      )}

      {!!milestones.length && (
        <React.Fragment>
          <PlannerTitleRow
            title={t("annualPlanner.milestones")}
            subtitle={<MilestoneLegend />}
            isCollapsed={isMilestonesCollapsed}
            onToggleCollapse={() => setIsMilestonesCollapsed((prev) => !prev)}
          />
          {!isMilestonesCollapsed && (
            <React.Fragment>
              <div className="planner-headers">
                <MilestoneRowHeader milestones={milestones} events={events} />
              </div>
              <div className="planner-body">
                {monthsData.map(({ month, numberOfWeeks }) => {
                  return (
                    <div role="month-column" key={month.format("YYYYMM")}>
                      <MilestoneCounter
                        numberOfWeeks={numberOfWeeks}
                        milestones={milestones}
                        units={units}
                        month={month}
                        startingCalendarDay={startingCalendarDay}
                      />
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          )}
        </React.Fragment>
      )}

      <PlannerTitleRow
        title={t("annualPlanner.trackedResults")}
        badge={
          <span className="phase-track-subtitle-text">
            {t("annualPlanner.trackedResultsWeeklyAverage")}
          </span>
        }
        isCollapsed={isStatsCollapsed}
        onToggleCollapse={() => setIsStatsCollapsed((prev) => !prev)}
      />
      {!isStatsCollapsed && (
        <React.Fragment>
          <div className="planner-headers">
            <AthleteStatsHeader athleteStatsHeaders={athleteStatsHeaders} />
          </div>
          <div className="planner-body">
            {monthsData.map(({ month, numberOfWeeks }) => {
              return (
                <div role="month-column" key={month.format("YYYYMM")}>
                  <StatsCard
                    month={month}
                    startingCalendarDay={startingCalendarDay}
                    numberOfWeeks={numberOfWeeks}
                    athleteStatsState={athleteStatsState}
                    athleteVolumesState={athleteVolumesState}
                    hideVolumeUnit
                    hasWideStatValue={hasWideStatValue}
                  />
                </div>
              );
            })}
          </div>
        </React.Fragment>
      )}

      <PlannerTitleRow
        title={t("annualPlanner.notesTitle")}
        isCollapsed={isNotesCollapsed}
        onToggleCollapse={() => setIsNotesCollapsed((prev) => !prev)}
      />
      {!isNotesCollapsed && (
        <React.Fragment>
          <div className="planner-headers">
            <div className="notes-row-header">
              <div className="phase-name-label">
                <span className="row-header">
                  {t("annualPlanner.notesTitle")}
                </span>
              </div>
            </div>
          </div>
          <div className="planner-body">
            {monthsData.map(({ month, numberOfWeeks }) => {
              const exactMonth = `${month.format("YYYYMM")}`;
              return (
                <div role="month-column" key={exactMonth}>
                  <PhasesDescriptionBubbles
                    numberOfWeeks={numberOfWeeks}
                    month={month}
                    startingCalendarDay={startingCalendarDay}
                    exactWeek={exactMonth}
                    phasesDescriptions={phasesDescriptions}
                    onSelectWeek={onSelectWeek}
                  />
                </div>
              );
            })}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
